import { useEffect, useState, useRef, useCallback } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtDate, fmtDateTime } from "../lib/date";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { toast } from "sonner";
import { ClipboardText, FloppyDisk, Pause, PencilSimple, Wrench, ArrowsClockwise } from "@phosphor-icons/react";

const PAUSE = [
  ["awaiting_parts", "Paused — Awaiting Parts"],
  ["awaiting_approval", "Paused — Awaiting Approval"],
  ["awaiting_external", "Paused — Awaiting External Work"],
  ["paused_other", "Paused — Other"],
];
const STATUS_LABEL = {
  draft: "Draft", open: "Draft", in_progress: "In Progress", completed: "Completed",
  reopened: "Reopened", void: "Void", cancelled: "Void",
  awaiting_parts: "Paused — Awaiting Parts", awaiting_approval: "Paused — Awaiting Approval",
  awaiting_external: "Paused — Awaiting External Work", paused_other: "Paused — Other",
};
const isPause = (s) => ["awaiting_parts", "awaiting_approval", "awaiting_external", "paused_other"].includes(s);
const BLANK = { entry_date: new Date().toISOString().slice(0, 10), start_time: "", finish_time: "", duration_minutes: "", work_done: "", findings: "", parts_note: "", measurements: "", notes: "", linked_fault_ids: [] };

export default function WorkLogPanel({ job, linkedFaults = [], isCompleted, onChange }) {
  const { hasPerm } = useAuth();
  const canWork = hasPerm("maintenance", "supervisor", "administrator");
  const canCorrect = hasPerm("supervisor", "administrator");
  const jid = job.id;
  const key = `wl-draft-${jid}`;
  const [form, setForm] = useState(() => { try { return { ...BLANK, ...JSON.parse(localStorage.getItem(key) || "{}") }; } catch { return { ...BLANK }; } });
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved
  const [lastSaved, setLastSaved] = useState(null);
  const [busy, setBusy] = useState(false);
  const [correct, setCorrect] = useState(null);
  const [statusForm, setStatusForm] = useState({ status: job.status, handover_note: job.handover_note || "", outstanding_work: job.outstanding_work || "", parts_awaiting: job.parts_awaiting || "" });
  const timer = useRef(null);

  const set = (k, v) => setForm((p) => { const n = { ...p, [k]: v }; return n; });
  // Autosave draft to localStorage so nothing is lost on navigation.
  useEffect(() => {
    setSaveState("saving");
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      localStorage.setItem(key, JSON.stringify(form));
      setSaveState("saved"); setLastSaved(new Date());
    }, 800);
    return () => timer.current && clearTimeout(timer.current);
  }, [form, key]);

  const log = job.work_log || [];
  const toggleFault = (fid) => set("linked_fault_ids", form.linked_fault_ids.includes(fid) ? form.linked_fault_ids.filter((x) => x !== fid) : [...form.linked_fault_ids, fid]);

  const addEntry = async () => {
    if (!(form.work_done || "").trim() && !(form.findings || "").trim()) { toast.error("Enter the work carried out or findings"); return; }
    setBusy(true);
    try {
      await api.post(`/maintenance/jobs/${jid}/work-log`, { ...form, duration_minutes: form.duration_minutes ? Number(form.duration_minutes) : null });
      localStorage.removeItem(key); setForm({ ...BLANK });
      toast.success("Work log entry added"); onChange && onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const saveStatus = async () => {
    setBusy(true);
    try {
      await api.post(`/maintenance/jobs/${jid}/status`, statusForm);
      toast.success("Status updated"); onChange && onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const paused = isPause(job.status);
  const faultRef = (fid) => { const lf = linkedFaults.find((l) => l.fault_id === fid); return lf ? `#${lf.ref}` : fid.slice(0, 6); };

  return (
    <div className="bg-white border-2 border-slate-300 rounded-lg" data-testid="work-log-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b-2 border-slate-200 bg-slate-50 flex-wrap gap-2">
        <h2 className="font-display text-xl font-black text-slate-900 flex items-center gap-2"><ClipboardText size={20} weight="bold" />Technician Work Log</h2>
        <span data-testid="job-status-label" className={`text-xs font-black uppercase px-2 py-1 rounded ${paused ? "bg-amber-100 text-amber-800" : job.status === "in_progress" ? "bg-blue-100 text-blue-800" : "bg-slate-100 text-slate-600"}`}>{STATUS_LABEL[job.status] || job.status}</span>
      </div>

      {/* Handover summary when paused/awaiting */}
      {paused && (
        <div data-testid="handover-panel" className="m-3 rounded-lg border-2 border-amber-300 bg-amber-50 p-3 text-sm">
          <div className="font-black uppercase text-amber-800 text-xs mb-1">Handover</div>
          <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1 text-slate-700">
            <div><b>Status:</b> {STATUS_LABEL[job.status]}</div>
            <div><b>Last worked on by:</b> {job.last_activity_by_name || "—"}</div>
            <div><b>Last activity:</b> {job.last_activity_at ? fmtDateTime(job.last_activity_at) : "—"}</div>
            <div><b>Parts awaiting:</b> {job.parts_awaiting || "—"}</div>
            <div className="sm:col-span-2"><b>Outstanding:</b> {job.outstanding_work || "—"}</div>
            {job.handover_note && <div className="sm:col-span-2"><b>Next action:</b> {job.handover_note}</div>}
          </div>
        </div>
      )}

      {/* Timeline (append-only, attributed) */}
      <div className="p-3 flex flex-col gap-2" data-testid="work-log-timeline">
        {log.length === 0 && <p className="text-slate-400 text-sm p-2">No work logged yet.</p>}
        {log.map((e) => (
          <div key={e.id} data-testid={`work-log-entry-${e.id}`} className={`rounded-lg border p-3 ${e.entry_type === "correction" ? "border-purple-300 bg-purple-50/50" : e.entry_type === "pause" ? "border-amber-200 bg-amber-50/40" : "border-slate-200"} ${e.corrected ? "opacity-70" : ""}`}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-sm font-bold text-slate-800">{e.technician_name || "—"}
                {e.entry_type === "correction" && <span className="ml-2 text-[10px] font-black uppercase bg-purple-200 text-purple-800 px-1.5 py-0.5 rounded">Correction</span>}
                {e.entry_type === "pause" && <span className="ml-2 text-[10px] font-black uppercase bg-amber-200 text-amber-800 px-1.5 py-0.5 rounded">Paused</span>}
                {e.corrected && <span className="ml-2 text-[10px] font-black uppercase bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">Corrected</span>}
              </div>
              <div className="text-xs text-slate-400 font-mono">{e.entry_date ? fmtDate(e.entry_date) : ""} {e.start_time || ""}{e.finish_time ? `–${e.finish_time}` : ""} {e.created_at ? `· ${fmtDateTime(e.created_at)}` : ""}</div>
            </div>
            {e.correction_reason && <div className="text-xs text-purple-700 mt-1"><b>Reason:</b> {e.correction_reason}</div>}
            {e.work_done && <div className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{e.work_done}</div>}
            {e.findings && <div className="text-sm text-slate-600 mt-1"><b>Findings:</b> {e.findings}</div>}
            {e.parts_note && <div className="text-sm text-slate-600"><b>Parts:</b> {e.parts_note}</div>}
            {e.measurements && <div className="text-sm text-slate-600"><b>Measurements:</b> {e.measurements}</div>}
            {e.notes && <div className="text-sm text-slate-500">{e.notes}</div>}
            {(e.linked_fault_ids || []).length > 0 && <div className="text-xs text-slate-500 mt-1">Relates to: {e.linked_fault_ids.map(faultRef).join(", ")}</div>}
            {e.duration_minutes ? <div className="text-xs text-slate-500">Time: {e.duration_minutes} min</div> : null}
            {canCorrect && e.entry_type !== "correction" && !e.corrected && (
              <button data-testid={`correct-entry-${e.id}`} onClick={() => setCorrect(e)} className="text-xs text-purple-600 font-semibold mt-1 flex items-center gap-1"><PencilSimple size={12} weight="bold" />Correct (audited)</button>
            )}
          </div>
        ))}
      </div>

      {/* Add entry (append-only) */}
      {canWork && !isCompleted && (
        <div className="border-t-2 border-slate-200 p-4 flex flex-col gap-2" data-testid="work-log-add">
          <div className="flex items-center justify-between">
            <h3 className="font-display font-black text-sm uppercase text-slate-700 flex items-center gap-2"><Wrench size={16} weight="bold" />Add your work entry</h3>
            <span data-testid="autosave-state" className="text-xs text-slate-400">{saveState === "saving" ? "Saving…" : saveState === "saved" ? `Saved${lastSaved ? ` · ${lastSaved.toLocaleTimeString()}` : ""}` : ""}</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div><label className="text-[11px] font-bold uppercase text-slate-500">Date</label><Input data-testid="wl-date" type="date" value={form.entry_date} onChange={(e) => set("entry_date", e.target.value)} className="h-9" /></div>
            <div><label className="text-[11px] font-bold uppercase text-slate-500">Start</label><Input data-testid="wl-start" type="time" value={form.start_time} onChange={(e) => set("start_time", e.target.value)} className="h-9" /></div>
            <div><label className="text-[11px] font-bold uppercase text-slate-500">Finish</label><Input data-testid="wl-finish" type="time" value={form.finish_time} onChange={(e) => set("finish_time", e.target.value)} className="h-9" /></div>
            <div><label className="text-[11px] font-bold uppercase text-slate-500">Duration (min)</label><Input data-testid="wl-duration" type="number" value={form.duration_minutes} onChange={(e) => set("duration_minutes", e.target.value)} className="h-9" /></div>
          </div>
          <div><label className="text-[11px] font-bold uppercase text-slate-500">Work carried out</label><Textarea data-testid="wl-work-done" value={form.work_done} onChange={(e) => set("work_done", e.target.value)} placeholder="What did you do?" /></div>
          <div className="grid sm:grid-cols-2 gap-2">
            <Textarea data-testid="wl-findings" value={form.findings} onChange={(e) => set("findings", e.target.value)} placeholder="Diagnosis / findings" />
            <Textarea data-testid="wl-parts" value={form.parts_note} onChange={(e) => set("parts_note", e.target.value)} placeholder="Parts fitted / removed" />
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            <Textarea data-testid="wl-measurements" value={form.measurements} onChange={(e) => set("measurements", e.target.value)} placeholder="Measurements / test results" />
            <Textarea data-testid="wl-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Notes" />
          </div>
          {linkedFaults.length > 0 && (
            <div>
              <label className="text-[11px] font-bold uppercase text-slate-500">Relates to issue(s)</label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {linkedFaults.map((lf) => (
                  <button key={lf.fault_id} type="button" data-testid={`wl-fault-${lf.fault_id}`} onClick={() => toggleFault(lf.fault_id)}
                    className={`text-xs font-bold px-2 py-1 rounded border-2 ${form.linked_fault_ids.includes(lf.fault_id) ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`}>#{lf.ref}</button>
                ))}
              </div>
            </div>
          )}
          <div className="flex justify-end">
            <Button data-testid="wl-add-btn" disabled={busy} onClick={addEntry} className="bg-blue-600 hover:bg-blue-700 font-bold"><FloppyDisk size={15} weight="bold" className="mr-1" />Save entry</Button>
          </div>
        </div>
      )}

      {/* Status / pause & handover */}
      {canWork && !isCompleted && (
        <div className="border-t-2 border-slate-200 p-4 flex flex-col gap-2" data-testid="job-status-control">
          <h3 className="font-display font-black text-sm uppercase text-slate-700 flex items-center gap-2"><Pause size={16} weight="bold" />Status &amp; handover</h3>
          <select data-testid="job-status-select" value={statusForm.status} onChange={(e) => setStatusForm({ ...statusForm, status: e.target.value })} className="h-10 border-2 border-slate-300 rounded px-2 text-sm">
            <option value="draft">Draft</option>
            <option value="in_progress">In Progress</option>
            {PAUSE.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          {isPause(statusForm.status) && (
            <div className="flex flex-col gap-2">
              <Textarea data-testid="handover-note" value={statusForm.handover_note} onChange={(e) => setStatusForm({ ...statusForm, handover_note: e.target.value })} placeholder="Handover / next action (e.g. When bearing arrives, fit, torque hub nut & road test)" />
              <div className="grid sm:grid-cols-2 gap-2">
                <Input data-testid="parts-awaiting" value={statusForm.parts_awaiting} onChange={(e) => setStatusForm({ ...statusForm, parts_awaiting: e.target.value })} placeholder="Parts awaiting" className="h-9" />
                <Input data-testid="outstanding-work" value={statusForm.outstanding_work} onChange={(e) => setStatusForm({ ...statusForm, outstanding_work: e.target.value })} placeholder="Outstanding work" className="h-9" />
              </div>
            </div>
          )}
          <div className="flex justify-end"><Button data-testid="job-status-save" disabled={busy} onClick={saveStatus} className="bg-slate-900 hover:bg-slate-800 font-bold"><ArrowsClockwise size={15} weight="bold" className="mr-1" />Update status</Button></div>
        </div>
      )}

      {correct && <CorrectDialog entry={correct} onClose={() => setCorrect(null)} onDone={() => { setCorrect(null); onChange && onChange(); }} />}
    </div>
  );
}

function CorrectDialog({ entry, onClose, onDone }) {
  const [f, setF] = useState({ reason: "", work_done: entry.work_done || "", findings: entry.findings || "", notes: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.reason.trim()) { toast.error("A reason is required"); return; }
    setBusy(true);
    try { await api.post(`/maintenance/work-log/${entry.id}/correct`, f); toast.success("Correction recorded (original preserved)"); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="correct-entry-dialog" className="bg-white">
        <DialogHeader><DialogTitle>Correct work log entry</DialogTitle>
          <DialogDescription>The original entry stays in the record; this appends an audited correction.</DialogDescription>
        </DialogHeader>
        <p className="text-xs text-slate-500">The original entry by {entry.technician_name} stays in the record; this adds an audited correction.</p>
        <Textarea data-testid="correct-reason" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} placeholder="Reason for correction (required)" />
        <Textarea data-testid="correct-work-done" value={f.work_done} onChange={(e) => setF({ ...f, work_done: e.target.value })} placeholder="Corrected work carried out" />
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="correct-submit" disabled={busy} onClick={submit} className="bg-purple-600 hover:bg-purple-700 font-bold">Save correction</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
